import LinkPreview from 'components/LinkPreview'
import RichTextArea from 'components/RichTextArea'
import { UploadItem } from '@wellesley/uploads'
import { useDispatch } from 'react-redux'
import getLinksFromText from 'helpers/getLinksFromText'
import PostCardSkeleton from 'components/Skeletons/PostCardSkeleton'
import { UploadFileProps } from '@wellesley/ui'
import { PostPrivacy, Card } from '@wellesley/openapi'
import { uploadsProgressSlice } from '@wellesley/uploads/slice/progressSlice'
import { FC, ChangeEvent, KeyboardEvent } from 'react'
import { useProfileSettings } from '@wellesley/settings'

import { TPostForm } from '../types'
import UploadDropzone from '../../../uploads/components/UploadDropzone'
import FollowersWarning from './FollowersWarning'
import { MediaContent } from '../../Feed/MediaContent'

type CreatePostSectionProps = {
  text: string
  card?: Card | null
  privacy: PostPrivacy
  uploads: UploadItem[]
  isLoading: boolean
  isFocused: boolean
  hasUploads: boolean
  autoFocus?: boolean
  isPreviewLink: boolean
  contentWarning: string
  userMentionSearchScope?: {
    groupId?: string
  }
  setText(s: string): void
  onAddMedia(props: UploadFileProps[]): void
  onToggleFocus(): void
  onHidePreviewLink(): void
  onRemoveMedia(blobUrl: string): Promise<void>
  onUpdateMedia(upload: UploadItem, altText: string): Promise<void>
  onEnterKeyDown(evt: KeyboardEvent<HTMLTextAreaElement>): void
  onTextAreaChange(evt: ChangeEvent<HTMLTextAreaElement>): void
  textAreaRef: React.RefObject<HTMLTextAreaElement>
} & Pick<TPostForm, 'placeholder'>

const CreatePostSection: FC<CreatePostSectionProps> = ({
  card,
  text,
  privacy,
  isFocused,
  isLoading,
  uploads,
  placeholder = 'Type your message here...',
  hasUploads,
  autoFocus,
  textAreaRef,
  isPreviewLink,
  contentWarning,
  userMentionSearchScope,
  setText,
  onAddMedia,
  onToggleFocus,
  onUpdateMedia,
  onRemoveMedia,
  onEnterKeyDown,
  onTextAreaChange,
  onHidePreviewLink,
}) => {
  const dispatch = useDispatch()
  const links = text && getLinksFromText(text)
  const { settings } = useProfileSettings()
  const isFollowAutoAccept = settings?.user?.follow?.autoAccept ?? false

  const handleRemoveMedia = (blobUrl: string) => {
    dispatch(uploadsProgressSlice.actions.resetProgress(blobUrl))
    void onRemoveMedia(blobUrl)
  }

  if (isLoading)
    return (
      <PostCardSkeleton
        className="w-full"
        showMedia={hasUploads}
        showAvatar={false}
        showFooter={false}
      />
    )

  return (
    <>
      {privacy === 'PRIVATE' && isFollowAutoAccept && <FollowersWarning />}
      <UploadDropzone addMedia={onAddMedia}>
        <div className="relative z-[4]">
          <RichTextArea
            rowsCount={3}
            name="create-post-section"
            autoFocus={autoFocus ?? true}
            disabled={isLoading}
            userMentionSearchScope={userMentionSearchScope}
            {...{ text, setText }}
            inputRef={textAreaRef}
            placeholder={placeholder}
            onKeyDown={onEnterKeyDown}
            onFocus={onToggleFocus}
            onChange={onTextAreaChange}
            className="no-scrollbar w-full resize-none rounded-none !p-0 leading-5 text-gray-700 !outline-none focus-visible:bg-transparent focus-visible:outline-none md:min-h-10"
          />
        </div>
        {hasUploads && (
          <MediaContent
            attachments={uploads}
            isSmallPreview={false}
            updateMedia={onUpdateMedia}
            removeMedia={handleRemoveMedia}
            summary={contentWarning}
          />
        )}
      </UploadDropzone>
      {links?.[0] && isPreviewLink && (
        <LinkPreview
          card={card}
          url={links[0]}
          text={text}
          showImage={!hasUploads}
          onClose={onHidePreviewLink}
          isFocusedField={isFocused}
        />
      )}
    </>
  )
}

export default CreatePostSection
